// Footer.jsx
import React, { useState, useEffect }  from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Facebook, Instagram, Clock, Shield, Award } from 'lucide-react';
import { TikTokIcon } from './TikTokIcon';
import { trackSocialClick } from '../utils/analytics';
import './Footer.css';
import brandingConfig from '../config/branding.config.js';
import businessConfig from '../config/business.config.js';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  // Add these new state variables
  const [secretClicks, setSecretClicks] = useState(0);
  const [secretTimer, setSecretTimer] = useState(null);

  // Add this function to handle the secret pattern
const handleSecretClick = (e) => {
  // Only detect clicks on the footer area
  if (e.target.classList.contains('footer-secret-area')) {
    // Clear any existing timer
    if (secretTimer) {
      clearTimeout(secretTimer);
    }
    
    // Increment click counter
    setSecretClicks(prevClicks => prevClicks + 1);
    
    // Set a timeout to reset the counter if user doesn't complete pattern quickly enough
    const timer = setTimeout(() => {
      setSecretClicks(0);
    }, 2000); // Reset after 2 seconds of inactivity
    
    setSecretTimer(timer);
    
    // If the pattern is completed (5 clicks), navigate to login
    if (secretClicks >= 4) { // Will become 5 after increment
      setSecretClicks(0);
      clearTimeout(timer);
      // Navigate to login page
      window.location.href = '/login';
    }
  }
};

// Clean up the timer when component unmounts
useEffect(() => {
  return () => {
    if (secretTimer) {
      clearTimeout(secretTimer);
    }
  };
}, [secretTimer]);
  
  return (
    <footer className="site-footer">
      <div className="footer-main">
        <div className="footer-container">
          {/* About Section */}
          <div className="footer-section footer-about">
            <h3 className="footer-brand">{brandingConfig.businessNameShort}</h3>
            <p className="footer-tagline">{businessConfig.business.tagline}</p>
            <p className="footer-description">
              {businessConfig.business.description}
            </p>
            <div className="footer-trust-badges">
              <div className="trust-badge">
                <Shield size={20} />
                <span>Secure Checkout</span>
              </div>
              <div className="trust-badge">
                <Award size={20} />
                <span>Quality Guaranteed</span>
              </div>
            </div>
          </div>

          {/* Services Section */}
          <div className="footer-section">
            <h4>Our Services</h4>
            <ul>
              {businessConfig.services.map((service, index) => (
                <li key={index}>{service}</li>
              ))}
            </ul>
          </div>

          {/* Contact Section */}
          <div className="footer-section">
            <h4>Get in Touch</h4>
            <ul className="contact-info">
              <li>
                <Phone size={16} className="icon" />
                <a href={`https://wa.me/${businessConfig.contact.phone}`}>{businessConfig.contact.phoneDisplay}</a>
              </li>
              <li>
                <Mail size={16} className="icon" />
                <a href={`mailto:${businessConfig.contact.email}`}>{businessConfig.contact.email}</a>
              </li>
              <li>
                <MapPin size={16} className="icon" />
                <span>{businessConfig.contact.address.full}</span>
              </li>
              <li>
                <Clock size={16} className="icon" />
                <span>{businessConfig.contact.hours.display}</span>
              </li>
            </ul>
          </div>

          {/* Business Info Section */}
          <div className="footer-section">
            <h4>Customer Care</h4>
            <ul>
              <li><Link to="/contact">Contact Us</Link></li>
              <li><Link to="/shipping">Shipping Info</Link></li>
              <li><Link to="/returns">Returns Policy</Link></li>
            </ul>
            <div className="social-links">
              {businessConfig.socialMedia.facebook.enabled && (
                <a
                  href={businessConfig.socialMedia.facebook.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                  onClick={() => trackSocialClick('facebook')}
                >
                  <Facebook size={20} />
                </a>
              )}
              {businessConfig.socialMedia.instagram.enabled && (
                <a
                  href={businessConfig.socialMedia.instagram.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  onClick={() => trackSocialClick('instagram')}
                >
                  <Instagram size={20} />
                </a>
              )}
              {businessConfig.socialMedia.tiktok.enabled && (
                <a
                  href={businessConfig.socialMedia.tiktok.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="TikTok"
                  onClick={() => trackSocialClick('tiktok')}
                >
                  <TikTokIcon size={20} />
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer Bottom */}
      <div className="footer-bottom">
        <div className="footer-bottom-content">
          <p className="copyright">&copy; {currentYear} {brandingConfig.businessNameShort}. All rights reserved.</p>
          <div className="footer-links-bottom">
            <Link to="/privacy">Privacy Policy</Link>
            <span className="separator">•</span>
            <Link to="/terms">Terms of Service</Link>
          </div>
        </div>
      </div>

      {/* Secret Admin Access */}
      <div
        className="footer-secret-area"
        onClick={handleSecretClick}
        style={{
          position: 'fixed',
          bottom: '10px',
          right: '10px',
          width: '40px',
          height: '40px',
          cursor: 'default',
          zIndex: 10
        }}
      />
    </footer>
  );
};

export default Footer;